/**
 * THE landing predicate of the tenant management SPA, as a hook.
 *
 * Moved verbatim out of routes.tsx (TASK-TSURF-LOGO-TO-CHOOSER-CHG-003) so the
 * route builders and the shell header resolve through ONE assembly of the same
 * three inputs. Before the move the header logo would have had to rebuild it
 * from the same parts, and a second assembly is the drift this epic exists to
 * remove: the day one of them gains an input, the logo and the router answer
 * differently for the same caller.
 *
 * It lives here, not in landing.ts, because it is React-bearing (it reads two
 * contexts), and landing.ts is pure by its own module comment: no React, no
 * QueryClient, callable from a test with an object literal.
 *
 * @module apps/tenant/useLandingPredicate
 * @see SPEC-TENANT-SURFACES-RULE-005(b)
 * @see TASK-TSURF-W3-LANDING-RESOLVER
 * @see TASK-TSURF-LOGO-TO-CHOOSER-CHG-003
 */
import { isEnabled } from "../../feature-gate";
import { usePermissionContext } from "../../store/PermissionContext";
import { usePagePresence, useSurfaceSet } from "../../surfaces";
import { resolvedUsable, type AgentStartConfig } from "./landing";

/**
 * The predicate every landing resolves through
 * (TASK-TSURF-W3-LANDING-RESOLVER): the release gate, the caller's permission
 * AND the resolved surface set as the navigation SHOWS it (`isShown`, D-TSG-23
 * point 3: a whole advanced page only while the holder's switch is on). It is a
 * subset of the `isUsable` `gateRoutes` filters the routes with, so a redirect
 * can still never name a sibling whose `<Route>` the builders omitted. A hook,
 * because the set is one.
 */
export function useLandingPredicate(): ReturnType<typeof resolvedUsable> {
  const { hasPermission } = usePermissionContext();
  const { isShown } = usePagePresence();
  return resolvedUsable({ isEnabled, hasPermission, isSurfaceUsable: isShown });
}

/**
 * The platform admin's agent start page (D-TSG-32), from the SAME surfaces read
 * the predicate above filters on - so a start page and the set it is checked
 * against always come from one answer. Null while that read is loading or has
 * failed: every landing then uses the default order.
 */
export function useAgentStart(): AgentStartConfig | null {
  return useSurfaceSet().agentStart;
}
